import { PrimaryButton } from "../components/primary-button";
import { SemanticPhrases } from "../components/semantic-phrases";
import { SiteFooter, SiteHeader } from "../components/site-header";
import { INTERFACE_ASSETS } from "../../domain/interface-assets";

const landingCopy = {
  kicker: "最近、人に厳しくなっていませんか。",
  description:
    "冷笑、論破、他責、老害化。日常とSNSに漏れ出す8つの劣化サインを、約3分で洗い出します。",
  patternsHeading: "あなたに漏れ出す、8つの劣化サイン",
  patternsDescription:
    "共感、利得、責任、制裁の4領域から、8つの行動傾向を読み解きます。",
  sampleType: "冷笑ウォッチャー",
  sampleComment: "応援ボタンより先に、失敗待ちの観客席を確保しています。",
  sampleDescription:
    "専用アート、強く出た行動、以前からの変化、今日から戻す一手まで順番に表示します。",
  insightHeading: "点数だけでは見えない、3つの判定",
  disclaimer:
    "この診断は、心理学の関連概念を参考にした娯楽的セルフチェックです。医療的・臨床的な診断ではなく、性格や原因を断定しません。回答時点の行動傾向としてお楽しみください。",
  storageAnswer:
    "一般公開では回答や属性をサーバーへ保存しません。診断中だけ同じタブに一時保存し、結果が確定したら回答内容を削除します。",
  closingHeading: "悪くなった自分に気づいた今が、戻しどきです。",
} as const;

const landingPhrases = {
  kicker: ["最近、", "人に厳しくなっていませんか。"],
  description: [
    "冷笑、論破、他責、老害化。",
    "日常とSNSに漏れ出す8つの劣化サインを、",
    "約3分で洗い出します。",
  ],
  patternsHeading: ["あなたに漏れ出す、", "8つの劣化サイン"],
  sampleType: ["冷笑ウォッチャー"],
  insightHeading: ["点数だけでは見えない、", "3つの判定"],
  disclaimerHeading: ["利用前に", "ご確認ください"],
  faqHeading: ["よくある質問"],
  closingHeading: ["悪くなった自分に気づいた今が、", "戻しどきです。"],
} as const;

const insights = [
  [
    "いま漏れ出している悪さ",
    "共感、利得、責任、制裁の4領域から、強く出ている行動傾向を示します。",
  ],
  [
    "前より悪くなったのか",
    "もともとの性格を決めつけず、自分で感じる以前からの変化を分けて確認します。",
  ],
  [
    "今日から戻すための一手",
    "行動風刺で終わらせず、今日から試せる小さな見直し方を提示します。",
  ],
] as const;

const insightHeadingPhrases = [
  ["いま漏れ出している悪さ"],
  ["前より悪くなったのか"],
  ["今日から戻すための一手"],
] as const;

const patterns = [
  ["01", ["共感摩耗"], "人の事情より先に、甘えと判定する。"],
  ["02", ["冷笑・嫉妬"], "挑戦を茶化し、失敗に少し安堵する。"],
  ["03", ["特権・優越"], "自分だけは例外だと思いはじめる。"],
  ["04", ["操作・搾取"], "人を目的達成の手段として扱う。"],
  ["05", ["硬直・不寛容"], "新しい価値観を検討前に却下する。"],
  ["06", ["他責・自己正当化"], "原因は外へ、言い訳は内から。"],
  ["07", ["攻撃・支配"], "論破と制裁で相手を従わせる。"],
  ["08", ["正義マウント"], "正しさを、改善より地位に使う。"],
] as const;

export function LandingRoute() {
  return (
    <div className="site-shell">
      <SiteHeader />
      <main>
        <section className="hero" aria-labelledby="hero-heading">
          <div className="hero__copy">
            <p className="eyebrow">PERSONALITY DECAY CHECK / 性格劣化診断</p>
            <p className="hero__kicker">
              <SemanticPhrases phrases={landingPhrases.kicker} />
            </p>
            <h1 id="hero-heading">
              <SemanticPhrases
                forceBreakAfter={[0]}
                phrases={["前より性格が悪くなったか、", "24問で暴きます。"]}
              />
            </h1>
            <p className="hero__description">
              <SemanticPhrases phrases={landingPhrases.description} />
            </p>
            <PrimaryButton to="/check">劣化レベルを測る</PrimaryButton>
            <p className="hero__meta">登録不要・約3分・全24問</p>
          </div>
          <picture className="landing-card">
            <source
              media="(max-width: 48rem)"
              srcSet={`/${INTERFACE_ASSETS.landingCard.mobile.avif}`}
              type="image/avif"
            />
            <source
              media="(max-width: 48rem)"
              srcSet={`/${INTERFACE_ASSETS.landingCard.mobile.webp}`}
              type="image/webp"
            />
            <source
              srcSet={`/${INTERFACE_ASSETS.landingCard.avif}`}
              type="image/avif"
            />
            <img
              alt={INTERFACE_ASSETS.landingCard.alt}
              decoding="async"
              fetchPriority="high"
              height={INTERFACE_ASSETS.landingCard.height}
              loading="eager"
              src={`/${INTERFACE_ASSETS.landingCard.webp}`}
              width={INTERFACE_ASSETS.landingCard.width}
            />
          </picture>
        </section>

        <section className="pattern-section" aria-labelledby="patterns-heading">
          <div className="section-heading">
            <p className="eyebrow">DECAY INDEX / 8 SIGNS</p>
            <h2 id="patterns-heading">
              <SemanticPhrases phrases={landingPhrases.patternsHeading} />
            </h2>
            <p>{landingCopy.patternsDescription}</p>
          </div>
          <ol className="pattern-grid">
            {patterns.map(([number, namePhrases, description]) => (
              <li key={number}>
                <span className="pattern-grid__number">{number}</span>
                <h3>
                  <SemanticPhrases phrases={namePhrases} />
                </h3>
                <p>{description}</p>
              </li>
            ))}
          </ol>
        </section>

        <section
          className="result-preview"
          aria-labelledby="result-preview-heading"
        >
          <div className="result-preview__art">
            <picture>
              <source
                media="(max-width: 48rem)"
                srcSet={`/${INTERFACE_ASSETS.resultSample.mobile.avif}`}
                type="image/avif"
              />
              <source
                media="(max-width: 48rem)"
                srcSet={`/${INTERFACE_ASSETS.resultSample.mobile.webp}`}
                type="image/webp"
              />
              <source
                srcSet={`/${INTERFACE_ASSETS.resultSample.avif}`}
                type="image/avif"
              />
              <img
                alt={INTERFACE_ASSETS.resultSample.alt}
                decoding="async"
                height={INTERFACE_ASSETS.resultSample.height}
                loading="lazy"
                src={`/${INTERFACE_ASSETS.resultSample.webp}`}
                width={INTERFACE_ASSETS.resultSample.width}
              />
            </picture>
          </div>
          <div className="result-preview__copy">
            <p className="eyebrow">RESULT SAMPLE / 解析見本</p>
            <h2 id="result-preview-heading">
              <SemanticPhrases
                forceBreakAfter={[0]}
                phrases={["点数だけで終わらない。", "悪くなり方まで出します。"]}
              />
            </h2>
            <div className="score-lockup" aria-label="結果例 性格劣化レベル72">
              <span>性格劣化レベル</span>
              <strong>72</strong>
              <span>/ 100</span>
            </div>
            <p className="result-preview__type">
              <SemanticPhrases phrases={landingPhrases.sampleType} />
            </p>
            <p>{landingCopy.sampleComment}</p>
            <p>{landingCopy.sampleDescription}</p>
          </div>
        </section>

        <section className="insight-section" aria-labelledby="insight-heading">
          <div className="section-heading">
            <p className="eyebrow">WHAT YOU GET / 3 JUDGMENTS</p>
            <h2 id="insight-heading">
              <SemanticPhrases phrases={landingPhrases.insightHeading} />
            </h2>
          </div>
          <div className="insight-grid">
            {insights.map(([heading, description], index) => (
              <article key={heading}>
                <span aria-hidden="true">
                  {String.fromCharCode(65 + index)}
                </span>
                <h3>
                  <SemanticPhrases phrases={insightHeadingPhrases[index]!} />
                </h3>
                <p>{description}</p>
              </article>
            ))}
          </div>
        </section>

        <aside className="disclaimer" aria-labelledby="disclaimer-heading">
          <p className="disclaimer__mark" aria-hidden="true">
            !
          </p>
          <div>
            <h2 id="disclaimer-heading">
              <SemanticPhrases phrases={landingPhrases.disclaimerHeading} />
            </h2>
            <p>{landingCopy.disclaimer}</p>
          </div>
        </aside>

        <section className="faq-section" aria-labelledby="faq-heading">
          <div className="section-heading">
            <p className="eyebrow">FAQ</p>
            <h2 id="faq-heading">
              <SemanticPhrases phrases={landingPhrases.faqHeading} />
            </h2>
          </div>
          <div className="faq-list">
            <details>
              <summary>これは心理テストや医療診断ですか？</summary>
              <p>
                いいえ。関連する心理学概念を参考にした独自設問による、娯楽的なセルフチェックです。
              </p>
            </details>
            <details>
              <summary>回答は保存されますか？</summary>
              <p>{landingCopy.storageAnswer}</p>
            </details>
            <details>
              <summary>低スコアなら「正常」ですか？</summary>
              <p>
                正常・善人とは判定しません。回答時点で表れた行動傾向としてお楽しみください。
              </p>
            </details>
          </div>
        </section>

        <section className="closing-cta" aria-labelledby="closing-heading">
          <p className="eyebrow">24 QUESTIONS / ABOUT 3 MINUTES</p>
          <h2 id="closing-heading">
            <SemanticPhrases phrases={landingPhrases.closingHeading} />
          </h2>
          <PrimaryButton to="/check">診断を始める</PrimaryButton>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
